'use client';

/**
 * BriefViewPopover Component
 *
 * Topbar popover that lists Astor's briefing for the day:
 *   - Header: ⚡ Brief view · {userName} + green-dot `live` chip + day/time stamp
 *   - Intro paragraph: mint-bold lead-in + body sentence
 *   - 3-tile KPI grid: To act on / Astor handled / SLA risk
 *   - "Your {N} priorities" list with mint/amber rank circles
 *   - Footer "Open full briefing →" button
 *
 * Visual spec source: design bundle shell.jsx (BriefViewPopover function).
 * Mint glow + border conventions mirror StageMapDrawer.tsx.
 */

import BoltIcon from '@mui/icons-material/Bolt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';
import { useI18n } from '../../hooks';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';

// ============================================================================
// Types
// ============================================================================

export interface BriefViewKpis {
  /** Count of items the user needs to act on today. */
  toActOn: number;
  /** Cutoff label interpolated into the "by X" sub-line (e.g. "11am"). */
  toActOnCutoff: string;
  /** Count of items Astor handled autonomously. */
  astorHandled: number;
  /** Start-of-day label interpolated into the "since X" sub-line (e.g. "5am"). */
  astorHandledStartTime: string;
  /** Count of SLA-at-risk items. 0 = "all clear", >0 = "{N} at risk". */
  slaRisk: number;
}

export interface BriefViewPriority {
  /** 1-based rank circle value. */
  rank: number;
  /** Primary row label (case/loan name). */
  title: string;
  /** Secondary line describing why this is on the list. */
  sub: string;
  /** Tone of the rank circle and accent. */
  tone: 'mint' | 'amber' | 'red';
  /** Optional click handler — when set the row renders as a `<button>`. */
  onOpen?: () => void;
}

export interface BriefViewPopoverProps {
  /** Display name of the briefed user (e.g. "Rachel V."). */
  userName: string;
  /** Timestamp shown in the header — formatted as US-style day + time. */
  liveAt: Date;
  /** Pre-rendered narrative (allows mint-bold lead-in markup). */
  intro: ReactNode;
  /** 3-tile KPI block at the top. */
  kpis: BriefViewKpis;
  /** Priority rows (max 5). When empty the `emptyState` is rendered. */
  priorities: BriefViewPriority[];
  /** Footer button click handler. When undefined the button is hidden. */
  onOpenFullBriefing?: () => void;
  /** Optional override for the empty-priorities state. */
  emptyState?: ReactNode;
  /** Style overrides. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Helpers
// ============================================================================

const MONO = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';

/**
 * Format the header timestamp as `Wed May 14 · 9:38a` — intentionally US-style
 * per the design spec, so no locale wiring.
 */
function formatLiveAt(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const mm = minutes.toString().padStart(2, '0');
  const suffix = hours < 12 ? 'a' : 'p';
  return `${datePart} · ${h12}:${mm}${suffix}`;
}

// ============================================================================
// Component
// ============================================================================

export function BriefViewPopover({
  userName,
  liveAt,
  intro,
  kpis,
  priorities,
  onOpenFullBriefing,
  emptyState,
  sx,
}: BriefViewPopoverProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const titleId = useId();

  // v3 design uses `primary.main` (#4ED9A4 in dark) for the brand mint accent
  // across the live-dot, popover border, intro lead-in, KPI accents, and the
  // `mint` priority tone — matches sibling popovers (NotificationsPopover,
  // AstorLiveToast) and avoids the side-by-side hue split that occurs when
  // some components pull from `success.main` (an intentionally offset shade).
  const mint = theme.palette.primary.main;
  const amber = theme.palette.warning.main;
  const red = theme.palette.error.main;
  const mintBorder = alpha(mint, isDark ? 0.22 : 0.18);
  const mintInsetGlow = alpha(mint, isDark ? 0.06 : 0.05);
  const tonePalette: Record<BriefViewPriority['tone'], string> = {
    mint,
    amber,
    red,
  };

  const slaSub =
    kpis.slaRisk === 0
      ? t('briefView.kpi.slaRiskSubAllClear')
      : t('briefView.kpi.slaRiskSubBreach', { count: kpis.slaRisk });

  return (
    <Paper
      role="dialog"
      aria-labelledby={titleId}
      elevation={0}
      sx={[
        {
          width: 420,
          maxWidth: '90vw',
          borderRadius: '11px',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: mintBorder,
          boxShadow: (theme) => `${theme.shadows[8]}, inset 0 0 0 1px ${mintInsetGlow}`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <BoltIcon aria-hidden sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
          <Typography
            id={titleId}
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('briefView.title')} · {userName}
          </Typography>
          <GlassChip
            label={t('briefView.live')}
            size="small"
            color="primary"
            aria-label={t('briefView.live')}
            sx={{ height: 18, fontSize: '0.75rem' }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontFamily: MONO,
            color: 'text.disabled',
            flexShrink: 0,
          }}
        >
          {formatLiveAt(liveAt)}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        {/* Intro */}
        <Typography variant="body2" component="p" sx={{ color: 'text.primary', lineHeight: 1.6, mb: 1.75 }}>
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t('briefView.intro.morning')}
          </Box>
          {intro}
        </Typography>

        {/* KPI grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 1,
            mb: 1.75,
          }}
        >
          <KpiTile
            label={t('briefView.kpi.toActOn')}
            value={kpis.toActOn}
            sub={t('briefView.kpi.toActOnSub', { cutoff: kpis.toActOnCutoff })}
            subColor={amber}
          />
          <KpiTile
            label={t('briefView.kpi.astorHandled')}
            value={kpis.astorHandled}
            valueColor={mint}
            sub={t('briefView.kpi.astorHandledSub', { startTime: kpis.astorHandledStartTime })}
            subColor={theme.palette.text.disabled}
          />
          <KpiTile
            label={t('briefView.kpi.slaRisk')}
            value={kpis.slaRisk}
            sub={slaSub}
            subColor={kpis.slaRisk === 0 ? mint : amber}
          />
        </Box>

        {/* Priorities */}
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            fontFamily: MONO,
            color: 'text.disabled',
            letterSpacing: '0.08em',
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t('briefView.priorities.header', { count: priorities.length })}
        </Typography>

        {priorities.length === 0 ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            {emptyState ?? (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {t('briefView.empty')}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {priorities.map((p) => (
              <PriorityRow key={p.rank} priority={p} accentColor={tonePalette[p.tone]} />
            ))}
          </Box>
        )}

        {onOpenFullBriefing && (
          <GlassButton
            variant="ghost"
            size="small"
            fullWidth
            onClick={onOpenFullBriefing}
            sx={{ mt: 1.25, justifyContent: 'center' }}
          >
            {t('briefView.openFull')} →
          </GlassButton>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface KpiTileProps {
  label: string;
  value: number;
  valueColor?: string;
  sub: string;
  subColor?: string;
}

function KpiTile({ label, value, valueColor, sub, subColor }: KpiTileProps) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: '7px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          fontFamily: MONO,
          color: 'text.disabled',
          letterSpacing: '0.08em',
          fontWeight: 600,
          lineHeight: 1,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          // Design spec calls for 18px mono KPI values. MUI's default `h6`
          // resolves to ~17px on the project type scale (per typography.ts),
          // which reads small against the surrounding labels. Lock the size
          // here so the tile renders at the spec'd 18px regardless of theme
          // overrides — matches design bundle shell.jsx KPI tile.
          fontFamily: MONO,
          fontSize: 18,
          fontWeight: 600,
          color: valueColor ?? 'text.primary',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: subColor ?? 'text.disabled',
          mt: 0.25,
        }}
      >
        {sub}
      </Typography>
    </Box>
  );
}

interface PriorityRowProps {
  priority: BriefViewPriority;
  accentColor: string;
}

function PriorityRow({ priority, accentColor }: PriorityRowProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentDim = alpha(accentColor, isDark ? 0.18 : 0.14);
  const isClickable = !!priority.onOpen;

  return (
    <Box
      // Render as <button> only when there's a real click handler — otherwise
      // a focusable element with no action is a screen-reader / keyboard trap
      // (same convention as StageIndicator.tsx Next-stage pill).
      {...(isClickable
        ? { component: 'button' as const, type: 'button' as const, onClick: priority.onOpen }
        : { component: 'div' as const })}
      sx={{
        width: '100%',
        px: 1.25,
        py: 1.125,
        borderRadius: '7px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        cursor: isClickable ? 'pointer' : 'default',
        fontFamily: 'inherit',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        minHeight: 44,
        '&:hover': isClickable
          ? {
              borderColor: accentColor,
              backgroundColor: alpha(accentColor, 0.06),
            }
          : undefined,
        '&:focus-visible': {
          outline: `2px solid ${accentColor}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: accentDim,
          color: accentColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: MONO,
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {priority.rank}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {priority.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {priority.sub}
        </Typography>
      </Box>
      {isClickable && <ChevronRightIcon aria-hidden sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />}
    </Box>
  );
}

BriefViewPopover.displayName = 'BriefViewPopover';
